import { formatDate } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { FormControl, UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom, forkJoin, Observable, Subscription } from 'rxjs';
import { API_BASE_URL_SERVICE } from '../../../kiene-core.config';
import { KieneBackendApiService } from '../../../services/backend-api/kiene-backend-api.service';
import { MessageService } from '../../../services/message-service/message.service';
import { KieneUtils } from '../../../utils/KieneUtils';
import { Client } from '../../client/classes/client';
import { ohneTierlisteKommentar, Tierliste } from '../../gruppe-tier/classes/tierliste';
import {
    AusstallenEmpfaengerDialogComponent,
    AusstallenEmpfaengerDialogResult,
} from '../ausstallen-empfaenger-dialog/ausstallen-empfaenger-dialog.component';
import {
    ExternEmpfaengerDisplayEntry,
    getExternEmpfaengerDisplayEntries,
    hasExternEmpfaenger,
    applyExternEmpfaengerPersistFields,
    applySchlachthofPersistField,
    mapBetriebskontakteTierlisteVerbindungenToKontakte,
    patchTierlisteFromExternEmpfaenger,
    filterAktiveBetriebskontakteTierlisteVerbindungen,
    istTierlisteDerselbeKunde,
    resolveTierlisteExtern,
    unwrapBetriebskontakteTierlisteReadResponse,
} from '../../gruppe-tier/tierliste-zielbetrieb-display.util';
import { GruppeTier } from '../../gruppe-tier/classes/gruppe-tier';
import { GruppeTierBulkOverwriteDialogComponent } from '../../gruppe-tier/gruppe-tier-bulk-overwrite-dialog/gruppe-tier-bulk-overwrite-dialog.component';
import {
    BulkOverwriteAnimalRow,
    BulkOverwriteFieldSpec,
    BulkOverwriteMode,
    animalHasFieldValue,
    getAnimalFieldValue,
    mapGruppeTierRecordToBulkRow,
    needsBulkOverwriteConfirm,
    resolveBulkPerTierValue,
} from '../../gruppe-tier/gruppe-tier-bulk-overwrite-dialog/gruppe-tier-bulk-overwrite-dialog';
import {
    MeineKontakteDialogInput,
    MeineKontakteDialogResult,
} from '../../meine-kontakte/meine-kontakte-dialog/meine-kontakte-dialog';
import { MeineKontakteDialogComponent } from '../../meine-kontakte/meine-kontakte-dialog/meine-kontakte-dialog.component';
import {
    BetriebskontaktAuswahlConfig,
    BetriebskontaktAuswahlComponent,
} from '../../stammdaten/betriebskontakte/betriebskontakt-auswahl.component';
import { Betriebskontakt } from '../../stammdaten/betriebskontakte/betriebskontakt';
import { BetriebskontaktCardConfig } from '../../stammdaten/betriebskontakte/betriebskontakt-card.component';
import { BetriebskontakteService } from '../../stammdaten/betriebskontakte/betriebskontakte.service';
import { Haendler } from '../../stammdaten/haendler/haendler';
import { HaendlerDetailsDialogComponent } from '../../stammdaten/haendler/haendler-details-dialog/haendler-details-dialog.component';
import { canEditHaendler as userCanEditHaendler } from '../../stammdaten/haendler/haendler-permissions';
import { AusstallenDialogInput, AusstallenDialogResult } from './ausstallen-dialog';
import { BenutzerBetriebskontakteTierlisteService } from '../../../services/backend-api/benutzer-betriebskontakte-tierliste.service';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';
import { AppService } from '../../../services/app/app.service';
import { StammdatenService } from '../../stammdaten/stammdaten.service';

type ContactSelector = 'Schlachthof' | 'Kontakt';
type WeightEntryMode = 'perAnimal' | 'samePerAnimal' | 'totalOnly' | null;
type HaendlerSourceMode = 'none' | 'haendler' | 'kontakt';

@Component({
    selector: 'kiene-ausstallen-dialog',
    templateUrl: './ausstallen-dialog.component.html',
    styleUrls: ['./ausstallen-dialog.component.scss'],
})
export class AusstallenDialogComponent implements OnInit, OnChanges, OnDestroy {
    /** Wenn true: im Dashboard-Stepper eingebettet; kein Material-Dialog-Rahmen; Schließen über Output. */
    @Input() embeddedInStepper = false;
    /** Kundenportal mobile: full-page layout (no MatDialog); header/back handled by parent route. */
    @Input() embeddedAsMobilePage = false;
    /** Mobile scan-first flow: form opens after scans to close the list. */
    @Input() mobileFinishMode = false;
    @Input() embeddedTitle = 'Ausstallen';
    @Input() embeddedCreateButtonLabel = 'Weiter';
    /** Stepper provides sticky footer (e.g. Liste abschliessen); hide embedded action bar. */
    @Input() hideEmbeddedFooter = false;

    /** Schritt-Daten, wenn `embeddedInStepper` (sonst MAT_DIALOG_DATA). */
    @Input() stepperDialogInput: AusstallenDialogInput | null = null;

    @Output() ausstallenCompleted = new EventEmitter<AusstallenDialogResult>();
    @Output() ausstallenCancelled = new EventEmitter<void>();
    /** Fired after auto-save or partial persist (datum / Empfänger) on an existing tierliste. */
    @Output() tierlistePersisted = new EventEmitter<number>();

    tierliste = new Tierliste();

    schlachthof: Client;
    zielbetrieb: Client;
    ausstallenderBetrieb: Client;
    /** Intern Umstallen: user explicitly cleared Zielbetrieb (do not fall back to tierliste IDs). */
    private zielbetriebCleared = false;

    kommentarCtrl = new UntypedFormControl();
    /** Kommentar, mit dem das Formular geladen wurde. Unverändert wird er nicht mitgespeichert. */
    private kommentarBeimLaden = '';
    datumCtrl = new UntypedFormControl();
    hitMeldenCtrl = new UntypedFormControl(1);
    gewichteImportierenCtrl = new FormControl<boolean>(false);
    kfzCtrl = new UntypedFormControl('');
    gleicheUnternehmensgruppe = false;
    isSaving = false;

    haendlerSourceMode: HaendlerSourceMode = 'none';
    haendlerCtrl = new UntypedFormControl(null);
    haendlerFavoriten: Haendler[] = [];
    haendlerFavoritenLoading = false;
    selectedHaendlerKontakte: Betriebskontakt[] = [];
    canEditHaendler = false;
    haendlerKontaktConfig: BetriebskontaktAuswahlConfig = {
        showLandwirte: true,
        showHaendler: true,
        showSchlachthoefe: true,
        showAll: true,
        multiSelect: false,
        compact: true,
        title: '',
        placeholder: 'Kontakt auswählen...',
        allowCreateNew: true,
    };
    haendlerKontaktCardConfig: BetriebskontaktCardConfig = {
        showActions: true,
        showMenu: false,
        showFavorit: false,
        showBadge: true,
        compact: false,
        selectedMode: true,
    };

    private hitMeldenSubscription?: Subscription;
    private vvvoSubscription?: Subscription;
    private haendlerFavoritenSubscription?: Subscription;
    private haendlerHydrating = false;

    // Neue Eigenschaften für Multi-Empfänger
    selectedKontakte: Betriebskontakt[] = [];
    tempSelectedKontakte: Betriebskontakt[] = [];
    /** Cached for template binding; avoids *ngFor re-creating nodes every change detection. */
    empfaengerDisplayEntries: ExternEmpfaengerDisplayEntry[] = [];
    showKontakteAuswahl = true;
    showSelectedKontakte = false;

    // Eigene Kontakt-IDs für die Button-Logik (basierend auf kunde_id)
    eigeneKontaktIds: number[] = [];
    selectedTiereGewichte: { tier: GruppeTier; gewichtCtrl: FormControl }[] = [];
    /** Meine + freigegebene Betriebe (kunde_id), fuer Gewichtsuebergabe beim Ausstallen. */
    gewichtErlaubteKundeIds = new Set<number>();
    weightEntryMode: WeightEntryMode = 'totalOnly';
    sameWeightCtrl = new FormControl();
    totalWeightCtrl = new FormControl();

    private autoSaveOnChangeEnabled = false;
    private autoSaveQueue: Promise<void> = Promise.resolve();

    /** Wait for in-flight embedded auto-save (datum/Empfänger) before closing the list. */
    async flushPendingAutoSave(): Promise<void> {
        await this.autoSaveQueue;
    }
    private gespeicherteKontakteLoaded = false;

    // Betriebskontakt-Auswahl Konfiguration für Kontakte (Händler + Zielbetrieb)
    kontakteConfig: BetriebskontaktAuswahlConfig = {
        showLandwirte: true,
        showHaendler: true,
        showSchlachthoefe: false,
        showAll: false,
        multiSelect: true,
        title: 'Kontakte auswählen',
        placeholder: 'Kontakte suchen...',
        compact: true,
        allowCreateNew: true, // Ermöglicht das Erstellen neuer Kontakte
        keepOpenOnSelect: true, // Dialog bleibt nach Auswahl offen
        betriebId: undefined, // Wird in ngOnInit gesetzt
    };

    // Betriebskontakt-Card Konfiguration für ausgewählte Kontakte
    kontakteCardConfig: BetriebskontaktCardConfig = {
        showActions: true,
        showMenu: true,
        showFavorit: false,
        showBadge: true,
        compact: true,
        selectedMode: true, // true = zeigt X zum Entfernen direkt
        showHinzufuegen: true, // Zeigt "Zu Kontakten hinzufügen" Button
    };

    // Schlachthof-Konfiguration (bleibt erhalten)
    schlachthofConfig: BetriebskontaktAuswahlConfig = {
        showLandwirte: false,
        showHaendler: false,
        showSchlachthoefe: true,
        showAll: false,
        multiSelect: false,
        title: 'Schlachthof auswählen',
        placeholder: 'Schlachthof suchen...',
        compact: true,
    };

    constructor(
        @Optional() public dialogRef: MatDialogRef<AusstallenDialogComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) private readonly injectedDialogData: AusstallenDialogInput | null,
        private dialog: MatDialog,
        private api: KieneBackendApiService,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl,
        private messageService: MessageService,
        private bbtService: BenutzerBetriebskontakteTierlisteService,
        private betriebskontakteService: BetriebskontakteService,
        private localStorageService: LocalStorageService,
        private appService: AppService,
        private stammdatenService: StammdatenService,
    ) {}

    get data(): AusstallenDialogInput {
        const d = this.stepperDialogInput ?? this.injectedDialogData;
        if (!d) {
            throw new Error('AusstallenDialogComponent: keine Daten (MAT_DIALOG_DATA oder stepperDialogInput).');
        }
        return d;
    }

    private closeHost(result: AusstallenDialogResult | undefined): void {
        if (this.embeddedInStepper || this.embeddedAsMobilePage) {
            if (result) {
                this.ausstallenCompleted.emit(result);
            } else {
                this.ausstallenCancelled.emit();
            }
            return;
        }
        this.dialogRef?.close(result);
    }

    /**
     * Lädt die eigenen Kontakte für die Plus-Button Logik
     */
    private async ladeEigeneKontakte(): Promise<void> {
        try {
            const eigeneKontakte = await this.betriebskontakteService.getBetriebskontakte().toPromise();
            this.eigeneKontaktIds = eigeneKontakte.records.map(k => k.kunde_id);
        } catch (error) {
            this.eigeneKontaktIds = [];
        }
    }

    /**
     * Generiert die Konfiguration für eine Kontakt-Card
     * Plus-Button wird nur angezeigt wenn der Kontakt von einem anderen Benutzer kommt
     */
    getKontaktCardConfig(kontakt: Betriebskontakt): any {
        const istEigenerKontakt = KieneUtils.includesId(this.eigeneKontaktIds, kontakt.kunde_id);
        return {
            ...this.kontakteCardConfig,
            showHinzufuegen: !istEigenerKontakt, // Nur anzeigen wenn NICHT eigener Kontakt
            istKontaktBereitsVorhanden: istEigenerKontakt,
        };
    }

    get useModalEmpfaengerPicker(): boolean {
        return (
            (this.embeddedInStepper || this.mobileFinishMode || this.embeddedAsMobilePage) && !this.data?.intern
        );
    }

    get showHitAbgangMelden(): boolean {
        if (this.mobileFinishMode) {
            return !this.istDerselbeKunde();
        }
        return this.embeddedInStepper && !this.istDerselbeKunde();
    }

    get showGewichteImportieren(): boolean {
        return this.showHitAbgangMelden && !this.data?.intern;
    }

    get showReklamationenWeitergeben(): boolean {
        return false;
    }

    get showFinishInfoBox(): boolean {
        return this.mobileFinishMode && !!this.tierliste?.kunde_einstallen;
    }

    istDerselbeKunde(): boolean {
        return istTierlisteDerselbeKunde(this.getTierlisteForExternCheck(), this.getGruppeKundeIdForTierlisteExtern());
    }

    private getTierlisteForExternCheck(): Tierliste {
        const zielKundeId =
            Number(this.tierliste?.kunde_id_einstallen) ||
            Number(this.zielbetrieb?.kunde_id) ||
            Number(this.data?.zielbetrieb?.kunde_id);
        if (zielKundeId <= 0) {
            return this.tierliste;
        }
        return { ...this.tierliste, kunde_id_einstallen: zielKundeId };
    }

    private getGruppeKundeIdForTierlisteExtern(): number | null {
        const fromGruppe = Number(this.data?.gruppe_abgang?.kunde_id);
        if (fromGruppe > 0) {
            return fromGruppe;
        }
        const fromBetrieb = Number(this.ausstallenderBetrieb?.kunde_id ?? this.tierliste?.kunde_id_abgang);
        return fromBetrieb > 0 ? fromBetrieb : null;
    }

    /** DB requires 0/1 when closing (status 162). Umstallen: 1. Ausstallen: nie. */
    private resolveReklamationenWeitergebenForClose(): number {
        if (!this.tierliste?.kunde_id_einstallen) {
            return 0;
        }
        if (this.data?.intern || this.gleicheUnternehmensgruppe) {
            return 1;
        }
        return 0;
    }

    get mobilePagePrimaryLabel(): string {
        if (this.mobileFinishMode) {
            return 'Liste abschließen';
        }
        return this.tierliste.tierliste_id ? 'Speichern' : 'Scans fortsetzen';
    }

    get empfaengerCardAriaLabel(): string {
        return 'Empfänger auswählen: Kontakt oder Schlachthof';
    }

    hasEmpfaengerSelection(): boolean {
        return hasExternEmpfaenger(this.tierliste, this.selectedKontakte, this.schlachthof ?? null);
    }

    getEmpfaengerDisplayLines(): string[] {
        return this.empfaengerDisplayEntries.map(entry => entry.name);
    }

    getEmpfaengerDisplayEntries(): ExternEmpfaengerDisplayEntry[] {
        return this.empfaengerDisplayEntries;
    }

    trackEmpfaengerDisplayEntry(index: number, entry: ExternEmpfaengerDisplayEntry): string {
        return `${index}:${entry.name}`;
    }

    trackEmpfaengerDetailLine(index: number, line: string): string {
        return `${index}:${line}`;
    }

    private rebuildEmpfaengerDisplayEntries(): void {
        this.empfaengerDisplayEntries = getExternEmpfaengerDisplayEntries(
            this.tierliste,
            this.selectedKontakte,
            this.schlachthof ?? null,
        );
    }

    async ngOnInit(): Promise<void> {
        // Händler-Auswahl bei internem Umstallen aktiviert lassen
        if (this.data?.intern) {
            this.showKontakteAuswahl = true;
        } else if (this.useModalEmpfaengerPicker) {
            this.showKontakteAuswahl = false;
            this.showSelectedKontakte = false;
        }

        // Debug-Log für die Config

        // Setze die Betriebs-ID aus dem currentClient für Admin-Modus
        const currentClient = this.localStorageService.getCurrentClient();
        if (currentClient?.betrieb_id) {
            this.kontakteConfig.betriebId = currentClient.betrieb_id;
        }

        // Aktiviere Admin-Modus wenn der Benutzer Admin ist
        this.kontakteConfig.adminMode = this.localStorageService.currentUserIsAdmin();

        this.canEditHaendler = userCanEditHaendler(this.localStorageService);
        this.haendlerKontaktConfig = {
            ...this.haendlerKontaktConfig,
            allowCreateNew: this.canEditHaendler,
        };

        // Lade eigene Kontakte für die Plus-Button Logik
        await this.ladeEigeneKontakte();
        await this.loadGewichtErlaubteBetriebe();

        // Zielbetrieb aus den Daten setzen
        if (this.data?.zielbetrieb) {
            this.zielbetrieb = this.data.zielbetrieb;
        }

        this.tierliste.mehrere_gruppen = 0;
        if (this.data?.tierliste) {
            this.tierliste = this.data.tierliste;
            this.uebernehmeKommentarAusListe(this.tierliste.kommentar);
            this.gewichteImportierenCtrl.setValue(KieneUtils.isApiOne(this.tierliste.gewichte_importieren));

            // Gespeicherte Kontakte laden, wenn eine bestehende Tierliste bearbeitet wird
            if (this.tierliste.tierliste_id) {
                await this.ladeGespeicherteKontakte(this.tierliste.tierliste_id);
            }
        } else {
            // neu
            if (this.data?.gruppe_abgang) {
                this.tierliste.gruppe_id_abgang = this.data.gruppe_abgang.gruppe_id;
                // Keine tierliste_id setzen für neue Tierlisten
                this.tierliste.tierliste_id = undefined;
            } else {
                this.tierliste.mehrere_gruppen = 1;
            }
        }

        if (this.tierliste?.tierliste_id) {
            this.ladeTiereAusAktuellerTierliste();
        } else if (!this.embeddedInStepper) {
            this.selectedTiereGewichte = (this.data?.tiere || [])
                .filter(t => Number(t?.gt_id) > 0)
                .map(t => ({
                    tier: t,
                    gewichtCtrl: new FormControl(this.getTierAbgangGewicht(t)),
                }));
            this.applyLoadedAbgangGesamtgewicht();
        }

        if (this.data?.ausstallenderBetrieb) {
            this.setAusstallenderBetrieb(this.data.ausstallenderBetrieb);
        } else if (!this.data?.gruppe_abgang) {
            const aktuellerBetrieb = this.localStorageService.getCurrentClient();
            if (aktuellerBetrieb) {
                this.setAusstallenderBetrieb(aktuellerBetrieb);
            }
        }

        if (this.data?.intern && this.zielbetrieb?.kunde_id) {
            this.applyInternZielbetriebIdsToTierliste();
        }

        this.ladeHaendlerFavoriten();

        if (this.tierliste?.kunde_id_schlachthof) {
            this.schlachthof = new Client();
            this.schlachthof.kunde_id = this.tierliste.kunde_id_schlachthof;
            this.schlachthof.bezeichnung = this.tierliste.kunde_schlachthof;
            this.schlachthof.zusatz = this.tierliste.kunde_zusatz_schlachthof;
            this.schlachthof.vvvo = this.tierliste.kunde_vvvo_schlachthof;
            this.schlachthof.strasse = this.tierliste.kunde_strasse_schlachthof;
            this.schlachthof.plz = this.tierliste.kunde_plz_schlachthof;
            this.schlachthof.ort = this.tierliste.kunde_ort_schlachthof;
        }

        if (this.data?.gruppe_abgang?.kunde_id && !this.tierliste.tierliste_id) {
            if (!this.schlachthof) {
                const params = new HttpParams().set('kunde_id', this.data.gruppe_abgang.kunde_id);
                this.api.get(this.apiBaseUrl + 'tierliste/read_last_schlachthof.php', params).subscribe({
                    next: (kunde: Client) => {
                        if (kunde) {
                            this.schlachthof = kunde;
                            this.rebuildEmpfaengerDisplayEntries();
                        }
                    },
                    error: err => {
                        this.messageService.errorMessage('Fehler!', err);
                    },
                });
            }
        }

        if (this.embeddedInStepper || this.mobileFinishMode) {
            this.datumCtrl.setValidators([Validators.required]);
            this.datumCtrl.updateValueAndValidity({ emitEvent: false });
        }

        this.applyDefaultAusstalldatum();
        if (this.mobileFinishMode) {
            this.kfzCtrl.setValue(this.tierliste?.kfz ?? '');
            this.loadGleicheUnternehmensgruppe();
        }
        if (this.embeddedInStepper && this.tierliste?.tierliste_id) {
            this.enableAutoSaveOnChange();
        }
        if (this.showHitAbgangMelden) {
            this.loadHitMeldenPreference();
        }
        this.rebuildEmpfaengerDisplayEntries();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['stepperDialogInput'] || !this.embeddedInStepper) {
            return;
        }
        const input = this.stepperDialogInput;
        if (!input) {
            return;
        }
        if (input.tierliste) {
            this.tierliste = input.tierliste;
        }
        if (input.zielbetrieb) {
            this.zielbetrieb = input.zielbetrieb;
            if (input.intern) {
                this.applyInternZielbetriebIdsToTierliste();
            }
        }
        if (this.showHitAbgangMelden) {
            this.loadHitMeldenPreference();
        }
    }

    ngOnDestroy(): void {
        this.hitMeldenSubscription?.unsubscribe();
        this.vvvoSubscription?.unsubscribe();
        this.haendlerFavoritenSubscription?.unsubscribe();
    }

    get showHaendlerFavoritenEmptyState(): boolean {
        return !this.haendlerFavoritenLoading && this.haendlerFavoriten.length === 0;
    }

    compareHaendler(haendler1: Haendler, haendler2: Haendler): boolean {
        return haendler1 && haendler2
            ? KieneUtils.sameId(haendler1.viehhaendler_id, haendler2.viehhaendler_id)
            : haendler1 === haendler2;
    }

    setHaendlerSourceMode(mode: HaendlerSourceMode): void {
        this.haendlerSourceMode = mode;
        if (mode !== 'haendler') {
            this.haendlerCtrl.setValue(null, { emitEvent: false });
        }
        if (mode !== 'kontakt') {
            this.selectedHaendlerKontakte = [];
        }
        this.persistHaendlerOnChange();
    }

    onHaendlerSelected(): void {
        if (this.haendlerHydrating) {
            return;
        }
        if (this.haendlerCtrl.value?.viehhaendler_id) {
            this.haendlerSourceMode = 'haendler';
            this.selectedHaendlerKontakte = [];
        }
        this.persistHaendlerOnChange();
    }

    onHaendlerKontaktSelected(kontakt: Betriebskontakt): void {
        if (!kontakt) {
            return;
        }
        this.selectedHaendlerKontakte = [kontakt];
        this.haendlerSourceMode = 'kontakt';
        this.haendlerCtrl.setValue(null, { emitEvent: false });
        this.persistHaendlerOnChange();
    }

    onHaendlerKontakteSelected(kontakte: Betriebskontakt[]): void {
        if (kontakte?.length > 0) {
            this.onHaendlerKontaktSelected(kontakte[0]);
        }
    }

    onHaendlerKontaktEntfernen(): void {
        this.selectedHaendlerKontakte = [];
        if (!this.haendlerCtrl.value?.viehhaendler_id) {
            this.haendlerSourceMode = 'none';
        }
        this.persistHaendlerOnChange();
    }

    openHaendlerVerwaltung(): void {
        if (!this.canEditHaendler) {
            return;
        }
        const dialogRef = this.dialog.open(HaendlerDetailsDialogComponent, { minWidth: '90vw' });
        dialogRef.afterClosed().subscribe({
            next: result => {
                if (result) {
                    this.ladeHaendlerFavoriten({ hydrate: false });
                    this.haendlerCtrl.setValue(result, { emitEvent: false });
                    this.haendlerSourceMode = 'haendler';
                    this.selectedHaendlerKontakte = [];
                    this.persistHaendlerOnChange();
                }
            },
        });
    }

    private persistHaendlerOnChange(): void {
        if (this.haendlerHydrating) {
            return;
        }
        this.persistAusstallenOnChange('haendler');
    }

    private applyHaendlerFieldsToTierliste(): void {
        if (this.haendlerSourceMode === 'haendler') {
            this.tierliste.viehhaendler_id = Number(this.haendlerCtrl.value?.viehhaendler_id || 0);
            this.tierliste.betriebskontakt_haendler_id = 0;
            return;
        }
        if (this.haendlerSourceMode === 'kontakt') {
            this.tierliste.viehhaendler_id = 0;
            this.tierliste.betriebskontakt_haendler_id = Number(this.selectedHaendlerKontakte[0]?.kunde_id || 0);
            return;
        }
        this.tierliste.viehhaendler_id = 0;
        this.tierliste.betriebskontakt_haendler_id = 0;
    }

    private ladeHaendlerFavoriten(options?: { hydrate?: boolean }): void {
        const hydrate = options?.hydrate !== false;
        this.haendlerFavoritenSubscription?.unsubscribe();
        this.haendlerFavoritenLoading = true;
        this.haendlerFavoritenSubscription = this.stammdatenService.loadHaendlerFavoriten().subscribe({
            next: haendlerFavoriten => {
                this.haendlerFavoriten = haendlerFavoriten.records ?? [];
                this.haendlerFavoritenLoading = false;
                if (hydrate) {
                    this.hydrateHaendlerSelection();
                }
            },
            error: () => {
                this.haendlerFavoritenLoading = false;
                if (hydrate) {
                    this.hydrateHaendlerSelection();
                }
            },
        });
    }

    private hydrateHaendlerSelection(): void {
        const viehhaendlerId = Number(this.tierliste?.viehhaendler_id || 0);
        const kontaktId = Number(this.tierliste?.betriebskontakt_haendler_id || 0);
        this.haendlerHydrating = true;

        if (viehhaendlerId > 0) {
            this.haendlerSourceMode = 'haendler';
            this.selectedHaendlerKontakte = [];
            const found = this.haendlerFavoriten.find(h => Number(h.viehhaendler_id) === viehhaendlerId);
            if (found) {
                this.haendlerCtrl.setValue(found, { emitEvent: false });
            } else {
                const haendler = new Haendler();
                haendler.viehhaendler_id = viehhaendlerId;
                haendler.name = this.tierliste.viehhaendler_name || `Händler ${viehhaendlerId}`;
                this.haendlerFavoriten = [haendler, ...this.haendlerFavoriten];
                this.haendlerCtrl.setValue(haendler, { emitEvent: false });
            }
            this.haendlerHydrating = false;
            return;
        }

        if (kontaktId > 0) {
            this.haendlerSourceMode = 'kontakt';
            this.haendlerCtrl.setValue(null, { emitEvent: false });
            if (Number(this.selectedHaendlerKontakte[0]?.kunde_id) === kontaktId) {
                this.haendlerHydrating = false;
                return;
            }
            this.betriebskontakteService.getBetriebskontaktById(kontaktId).subscribe({
                next: kontakt => {
                    this.selectedHaendlerKontakte = kontakt ? [kontakt] : [];
                    this.haendlerHydrating = false;
                },
                error: () => {
                    this.haendlerHydrating = false;
                },
            });
            return;
        }

        this.haendlerSourceMode = 'none';
        this.haendlerCtrl.setValue(null, { emitEvent: false });
        this.selectedHaendlerKontakte = [];
        this.haendlerHydrating = false;
    }

    private loadGleicheUnternehmensgruppe(): void {
        this.vvvoSubscription?.unsubscribe();
        this.vvvoSubscription = this.stammdatenService
            .istBetriebInKundenportal(Number(this.tierliste?.kunde_id_einstallen || 0))
            .subscribe({
                next: gefunden => {
                    this.gleicheUnternehmensgruppe = gefunden;
                },
                error: () => {
                    this.gleicheUnternehmensgruppe = false;
                },
            });
    }

    private loadHitMeldenPreference(): void {
        this.hitMeldenSubscription?.unsubscribe();
        this.hitMeldenSubscription = this.api.getSpeicher('hit_melden').subscribe({
            next: response => {
                if (response != null && response !== undefined) {
                    this.hitMeldenCtrl.setValue(response);
                }
            },
            error: err => {
                this.messageService.errorMessage('Fehler beim Laden der HIT-Melden-Einstellung!', err);
            },
        });
    }

    saveHitMeldenPreference(): void {
        if (!this.showHitAbgangMelden) {
            return;
        }
        const value = this.hitMeldenCtrl.value;
        this.api.setSpeicher('hit_melden', value).subscribe({
            error: err => {
                this.messageService.errorMessage('Fehler beim Speichern der HIT-Melden-Einstellung!', err);
            },
        });
    }

    /** Dashboard stepper: validate + build tierliste payload for status-162 close (parent orchestrates). */
    prepareForEmbeddedAbschluss(): Tierliste | null {
        if (!this.validateBeforeSave()) {
            return null;
        }
        this.applyAbgangGewichteToTierliste();
        this.applyFormFieldsToTierliste();
        return { ...this.tierliste };
    }

    getEmbeddedAbschlussTiere(): GruppeTier[] {
        const gewichtByGtId = this.getAbgangGewichtByGtId();
        return (this.data?.tiere || []).map(t => {
            const gtId = Number(t?.gt_id);
            const gewicht = gtId > 0 ? gewichtByGtId.get(gtId) : undefined;
            if (gewicht === undefined) {
                return { ...t };
            }
            return { ...t, abgangsgewicht: gewicht } as GruppeTier;
        });
    }

    /**
     * Persist per-animal Abgangsgewichte before the list is closed (status 162).
     * Returns false when the user cancels the overwrite dialog.
     */
    async persistAbgangsgewichteIfNeeded(): Promise<boolean> {
        const mode = await this.resolveAbgangGewichtWriteMode();
        if (mode === 'cancel') {
            return false;
        }
        this.applyAbgangGewichtWriteMode(mode);
        this.applyAbgangGewichteToTierliste();
        await this.speichereAbgangsgewichteFuerTiere();
        return true;
    }

    async persistKontakteIfNeeded(): Promise<void> {
        if (this.data?.intern) {
            return;
        }
        const tierlisteId = Number(this.tierliste?.tierliste_id || 0);
        if (tierlisteId <= 0) {
            return;
        }
        await this.flushPendingAutoSave();
        if (!this.gespeicherteKontakteLoaded) {
            await this.ladeGespeicherteKontakte(tierlisteId);
        }
        if (this.selectedKontakte.length === 0) {
            return;
        }
        await this.speichereKontakteInTabelle(tierlisteId);
    }

    private parseAusstalldatum(value: Date | string | null | undefined): Date | null {
        if (value == null || value === '') {
            return null;
        }
        const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
        if (Number.isNaN(date.getTime())) {
            return null;
        }
        date.setHours(0, 0, 0, 0);
        return date;
    }

    /** MySQL DATE via API expects yyyy-MM-dd, not a JSON Date object. */
    private formatDatumForApi(value: Date | string | null | undefined): string | null {
        const parsed = this.parseAusstalldatum(value);
        if (!parsed) {
            return null;
        }
        return formatDate(parsed, 'yyyy-MM-dd', 'de', 'Europe/Berlin');
    }

    /**
     * Nur setzen, wenn der Benutzer den Kommentar in diesem Formular geändert hat.
     * Sonst behält die Prozedur den Datenbankstand (ein anderer Schritt hat ihn oft schon neu gesetzt).
     */
    kommentarOverrideForSave(): string | undefined {
        const aktuell = String(this.kommentarCtrl.value ?? '');
        if (aktuell === this.kommentarBeimLaden) {
            return undefined;
        }
        return aktuell;
    }

    private uebernehmeKommentarAusListe(kommentar: string | null | undefined): void {
        this.kommentarBeimLaden = String(kommentar ?? '');
        this.kommentarCtrl.setValue(this.kommentarBeimLaden, { emitEvent: false });
    }

    private payloadFuerUpdate(payload: Tierliste): Tierliste {
        const override = this.kommentarOverrideForSave();
        if (override === undefined) {
            return ohneTierlisteKommentar(payload);
        }
        return { ...payload, kommentar: override };
    }

    private kommentarNachSpeichernUebernehmen(liste: Tierliste | null | undefined): void {
        if (this.kommentarOverrideForSave() !== undefined) {
            this.kommentarBeimLaden = String(this.kommentarCtrl.value ?? '');
            return;
        }
        this.uebernehmeKommentarAusListe(liste?.kommentar ?? this.kommentarBeimLaden);
    }

    private applyFormFieldsToTierliste(): void {
        const override = this.kommentarOverrideForSave();
        if (override !== undefined) {
            this.tierliste.kommentar = override;
        }
        this.tierliste.datum_abgang = this.formatDatumForApi(this.datumCtrl.value) as unknown as Date;
        this.tierliste.haendler_uebernehmen = 0;
        if (this.ausstallenderBetrieb) {
            this.tierliste.kunde_id_abgang = this.ausstallenderBetrieb.kunde_id;
        }
        if (!this.tierliste.kunde_id_abgang && KieneUtils.isApiOne(this.tierliste.mehrere_gruppen)) {
            const fallbackBetrieb = this.ausstallenderBetrieb ?? this.localStorageService.getCurrentClient();
            if (fallbackBetrieb?.kunde_id) {
                this.tierliste.kunde_id_abgang = fallbackBetrieb.kunde_id;
                this.tierliste.kunde_abgang = fallbackBetrieb.bezeichnung_final ?? fallbackBetrieb.bezeichnung ?? '';
                this.tierliste.kunde_zusatz_abgang = fallbackBetrieb.zusatz_final ?? fallbackBetrieb.zusatz ?? '';
            }
        }
        applySchlachthofPersistField(this.tierliste, this.schlachthof);
        this.applyHaendlerFieldsToTierliste();
        if (this.data?.intern) {
            this.applyInternZielbetriebIdsToTierliste();
        } else {
            applyExternEmpfaengerPersistFields(this.tierliste, this.selectedKontakte);
        }
        if (this.istDerselbeKunde()) {
            this.tierliste.abgang_melden = 0;
        } else if (this.showHitAbgangMelden) {
            this.tierliste.abgang_melden = Number(this.hitMeldenCtrl.value ?? 1);
        }
        this.tierliste.gewichte_importieren = this.showGewichteImportieren && this.gewichteImportierenCtrl.value ? 1 : 0;
        if (this.mobileFinishMode) {
            this.tierliste.kfz = this.kfzCtrl.value;
            this.tierliste.status_id = 162;
            this.tierliste.import_einstallen = this.tierliste.kunde_id_einstallen ? 1 : 0;
            this.tierliste.reklamationen_weitergeben = this.resolveReklamationenWeitergebenForClose();
            return;
        }
        this.tierliste.status_id = Number(this.tierliste.status_id) > 0 ? this.tierliste.status_id : 161;
    }

    /** Use saved Ausstalldatum when present; otherwise default to today. */
    private applyDefaultAusstalldatum(): void {
        const saved = this.parseAusstalldatum(this.tierliste?.datum_abgang);
        const value =
            saved ??
            (() => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return today;
            })();
        this.datumCtrl.setValue(value, { emitEvent: false });
    }

    get canSave(): boolean {
        return !this.saveBlockedMessage;
    }

    get saveBlockedMessage(): string | null {
        if (!this.hasRequiredAusstalldatum()) {
            return this.getRequiredAusstalldatumMessage();
        }
        const tiereMessage = this.getRequiredTiereMessage();
        if (tiereMessage) {
            return tiereMessage;
        }
        if (!this.hasRequiredEmpfaengerSelection()) {
            return this.getRequiredEmpfaengerSelectionMessage();
        }
        return null;
    }

    get hasTiereOnTierliste(): boolean {
        return this.getTiereCountOnTierliste() > 0;
    }

    get hasZielbetriebSelected(): boolean {
        if (this.zielbetriebCleared) {
            return false;
        }
        if (Number(this.zielbetrieb?.kunde_id) > 0) {
            return true;
        }
        if (this.data?.intern) {
            return Number(this.tierliste?.kunde_id_einstallen) > 0;
        }
        return this.getZielKundeIdForSave() !== null;
    }

    private enableAutoSaveOnChange(): void {
        this.autoSaveOnChangeEnabled = true;
    }

    onAusstalldatumChanged(): void {
        if (!this.autoSaveOnChangeEnabled || !this.embeddedInStepper) {
            return;
        }
        if (!this.hasRequiredAusstalldatum()) {
            return;
        }
        void this.persistAusstallenOnChange('datum');
    }

    private persistAusstallenOnChange(field: 'datum' | 'empfaenger' | 'zielbetrieb' | 'haendler'): void {
        if (!this.autoSaveOnChangeEnabled || !this.tierliste?.tierliste_id || this.isSaving) {
            return;
        }
        if (field === 'datum' && !this.hasRequiredAusstalldatum()) {
            return;
        }
        this.autoSaveQueue = this.autoSaveQueue
            .then(() => this.executeAutoSave(field))
            .catch(() => undefined);
    }

    private async executeAutoSave(field: 'datum' | 'empfaenger' | 'zielbetrieb' | 'haendler'): Promise<void> {
        const tierlisteId = this.tierliste.tierliste_id;
        if (!tierlisteId) {
            return;
        }

        try {
            if (field === 'empfaenger') {
                await this.persistEmpfaengerLikeModal([...this.selectedKontakte]);
                return;
            }

            this.applyFormFieldsToTierliste();

            const payload = { ...this.tierliste };
            payload.status_id = Number(payload.status_id) > 0 ? payload.status_id : 161;
            payload.datum_abgang = this.formatDatumForApi(this.datumCtrl.value) as unknown as Date;

            const liste = await firstValueFrom(
                this.api.post(this.apiBaseUrl + 'tierliste/update_kundenportal.php', this.payloadFuerUpdate(payload), undefined),
            );
            this.kommentarNachSpeichernUebernehmen(liste);
            if (liste) {
                this.tierliste = { ...this.tierliste, ...liste };
                const savedDatum = this.parseAusstalldatum(this.tierliste.datum_abgang);
                this.datumCtrl.setValue(savedDatum, { emitEvent: false });
            }
            this.emitTierlistePersisted(tierlisteId);
        } catch (error) {
            this.messageService.errorMessage('Änderungen konnten nicht gespeichert werden.', error);
        }
    }

    private emitTierlistePersisted(tierlisteId?: number): void {
        const id = Number(tierlisteId ?? this.tierliste?.tierliste_id);
        if (id > 0) {
            this.tierlistePersisted.emit(id);
        }
    }

    /** Same persistence as `AusstallenEmpfaengerDialogComponent.save()`. */
    private async persistEmpfaengerLikeModal(kontakte: Betriebskontakt[]): Promise<void> {
        const tierlisteId = this.tierliste.tierliste_id;
        if (!tierlisteId) {
            return;
        }

        const effectiveKontakte = [...kontakte];

        this.applyEmpfaengerFieldsToTierliste(effectiveKontakte);
        this.applyHaendlerFieldsToTierliste();

        const liste = await firstValueFrom(
            this.api.post(
                this.apiBaseUrl + 'tierliste/update_kundenportal.php',
                this.payloadFuerUpdate(this.tierliste),
                undefined,
            ),
        );
        this.kommentarNachSpeichernUebernehmen(liste);
        const savedTierlisteId = liste?.tierliste_id ?? tierlisteId;

        await this.speichereKontakteInTabelle(savedTierlisteId, effectiveKontakte, { allowClearAll: true });

        const params = new HttpParams().set('tierliste_id', String(savedTierlisteId));
        const fresh = await firstValueFrom(
            this.api.get(this.apiBaseUrl + 'tierliste/read_one_kundenportal.php', params),
        );
        this.tierliste = patchTierlisteFromExternEmpfaenger(fresh, effectiveKontakte, this.schlachthof ?? null);
        this.selectedKontakte = [...effectiveKontakte];
        this.tempSelectedKontakte = [...effectiveKontakte];

        if (!this.schlachthof && effectiveKontakte.length === 0) {
            this.tierliste.kunde_zielbetrieb = null;
            this.tierliste.kunde_zusatz_zielbetrieb = null;
            this.tierliste.kunde_vvvo_zielbetrieb = null;
            this.tierliste.kunde_strasse_zielbetrieb = null;
            this.tierliste.kunde_plz_zielbetrieb = null;
            this.tierliste.kunde_ort_zielbetrieb = null;
            this.tierliste.kunde_einstallen = null;
            this.tierliste.kunde_zusatz_einstallen = undefined;
        }
        this.rebuildEmpfaengerDisplayEntries();
        this.emitTierlistePersisted(savedTierlisteId);
    }

    private applyEmpfaengerFieldsToTierliste(kontakte: Betriebskontakt[]): void {
        applySchlachthofPersistField(this.tierliste, this.schlachthof);
        if (this.data?.intern) {
            this.applyInternZielbetriebIdsToTierliste();
            return;
        }
        applyExternEmpfaengerPersistFields(this.tierliste, kontakte);
    }

    private applyInternZielbetriebIdsToTierliste(): void {
        if (this.zielbetriebCleared) {
            this.tierliste.kunde_id_einstallen = null as unknown as number;
            this.tierliste.kunde_id_zielbetrieb = null as unknown as number;
            this.clearInternZielbetriebDisplayFields();
            return;
        }
        const zielId = Number(this.zielbetrieb?.kunde_id);
        if (zielId > 0) {
            this.tierliste.kunde_id_einstallen = zielId;
            this.tierliste.kunde_id_zielbetrieb = zielId;
            this.tierliste.tierliste_extern = resolveTierlisteExtern(
                this.tierliste,
                this.getGruppeKundeIdForTierlisteExtern(),
            );
        }
    }

    private clearInternZielbetriebDisplayFields(): void {
        this.tierliste.kunde_einstallen = null as unknown as string;
        this.tierliste.kunde_zusatz_einstallen = undefined;
        this.tierliste.kunde_vvvo_einstallen = undefined;
        this.tierliste.kunde_strasse_einstallen = undefined;
        this.tierliste.kunde_plz_einstallen = undefined;
        this.tierliste.kunde_ort_einstallen = undefined;
    }

    async save(): Promise<void> {
        if (!this.validateBeforeSave()) {
            return;
        }
        if (this.embeddedInStepper) {
            return;
        }
        const mode = await this.resolveAbgangGewichtWriteMode();
        if (mode === 'cancel') {
            return;
        }
        this.applyAbgangGewichtWriteMode(mode);
        this.isSaving = true;
        const gesamtAbgang = this.applyAbgangGewichteToTierliste();
        this.applyFormFieldsToTierliste();

        // update
        let obs: Observable<Tierliste>;
        if (!this.tierliste.tierliste_id) {
            this.tierliste.umstallen = this.data?.intern ? 1 : 0;
        }
        if (this.tierliste.tierliste_id) {
            obs = this.api.post(
                this.apiBaseUrl + 'tierliste/update_kundenportal.php',
                this.payloadFuerUpdate(this.tierliste),
                undefined,
            );
        } else {
            obs = this.api.post(this.apiBaseUrl + 'tierliste/create_kundenportal.php', this.tierliste, undefined);
        }
        obs.subscribe({
            next: async (liste: Tierliste) => {
                this.kommentarNachSpeichernUebernehmen(liste);
                try {
                    // Kontakte in der benutzer_betriebskontakte_tierliste Tabelle speichern
                    // Speichern für alle Tierlisten (auch interne Umstallungen)
                    // Auch mit leerem Array speichern (alle Kontakte werden dann gelöscht)
                    await this.speichereKontakteInTabelle(liste.tierliste_id, this.selectedKontakte, {
                        allowClearAll: true,
                    });

                    if (!this.data?.intern) {
                        const params = new HttpParams().set('tierliste_id', String(liste.tierliste_id));
                        const fresh = await firstValueFrom(
                            this.api.get(this.apiBaseUrl + 'tierliste/read_one_kundenportal.php', params),
                        );
                        this.tierliste = patchTierlisteFromExternEmpfaenger(
                            fresh,
                            this.selectedKontakte,
                            this.schlachthof ?? null,
                        );
                        this.kommentarNachSpeichernUebernehmen(this.tierliste);
                    }

                    // Nach dem Speichern die Kontakte neu laden, damit sie beim Öffnen der Tierliste verfügbar sind
                    await this.ladeGespeicherteKontakte(liste.tierliste_id);
                    this.emitTierlistePersisted(liste.tierliste_id);

                    const gewichtByGtId = this.getAbgangGewichtByGtId();
                    const tiereMitGewichten: GruppeTier[] = (this.data?.tiere || []).map(t => {
                        const gtId = Number(t?.gt_id);
                        const gewicht = gtId > 0 ? gewichtByGtId.get(gtId) : undefined;
                        if (gewicht === undefined) {
                            return { ...t };
                        }
                        return { ...t, abgangsgewicht: gewicht } as GruppeTier;
                    });
                    const finishSave = () => {
                        if (this.mobileFinishMode) {
                            this.saveHitMeldenPreference();
                            this.messageService.infoMessage('Die Ausstallliste wurde erfolgreich abgeschlossen!');
                        } else {
                            this.messageService.infoMessage('Die Ausstallliste wurde erfolgreich angelegt!');
                        }
                        this.isSaving = false;
                        const persistedListe = { ...this.tierliste, ...liste };
                        this.closeHost(new AusstallenDialogResult(persistedListe, tiereMitGewichten));
                    };
                    const gewichtTransferErlaubt = this.isGewichtTransferErlaubt();
                    const shouldPersistPerTierAbgangsgewichte =
                        this.selectedTiereGewichte.length > 0 &&
                        (this.usesIndividualWeightMode() || gesamtAbgang !== null);
                    const saveGewichtePromise = shouldPersistPerTierAbgangsgewichte
                        ? this.speichereAbgangsgewichteFuerTiere()
                        : Promise.resolve();
                    saveGewichtePromise
                        .then(() => {
                            if (!gewichtTransferErlaubt && this.data?.intern && this.usesIndividualWeightMode()) {
                                this.messageService.infoMessage(
                                    'Zielbetrieb ist weder eigener noch freigegebener Betrieb. Gewichte werden nicht uebergeben.',
                                );
                            }
                            finishSave();
                        })
                        .catch(error => {
                            this.messageService.errorMessage('Gewichte konnten nicht vollstaendig gespeichert werden!', error);
                            this.isSaving = false;
                            this.closeHost(new AusstallenDialogResult(liste, tiereMitGewichten));
                        });
                } catch (error) {
                    this.messageService.errorMessage('Fehler beim Speichern der Kontakte!', error);
                    this.isSaving = false;
                }
            },
            error: err => {
                this.messageService.errorMessage('Fehler!', err);
                this.isSaving = false;
            },
        });
    }

    close() {
        this.closeHost(undefined);
    }

    ladeLetzteGewichte(): void {
        if (!this.selectedTiereGewichte?.length || !this.data?.gruppe_abgang?.gruppe_id) {
            return;
        }
        const gtIds = this.selectedTiereGewichte
            .map(item => Number(item?.tier?.gt_id))
            .filter(id => id > 0);
        if (!gtIds.length) {
            return;
        }

        let params = new HttpParams();
        params = params.set('gruppe_id', this.data.gruppe_abgang.gruppe_id);
        params = params.set('gt_ids', gtIds.join(','));
        this.api.getAll(this.apiBaseUrl + 'gruppe/read_gt_alte_gewichte.php', params).subscribe({
            next: response => {
                const records = response?.records || [];
                const weightByGtId = new Map<number, number>();
                for (const rec of records) {
                    const gtId = Number(rec?.gt_id);
                    const w = this.toValidGewicht(rec?.gewicht_alt);
                    if (gtId > 0 && w !== null) {
                        weightByGtId.set(gtId, w);
                    }
                }
                const values = [...weightByGtId.values()];
                let gesetzt = 0;
                if (this.weightEntryMode === 'totalOnly' && values.length) {
                    const sum = Math.round(values.reduce((a, b) => a + b, 0) * 100) / 100;
                    this.totalWeightCtrl.setValue(sum);
                    gesetzt = values.length;
                } else if (this.weightEntryMode === 'samePerAnimal' && values.length) {
                    const avg = Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 100) / 100;
                    this.sameWeightCtrl.setValue(avg);
                    gesetzt = values.length;
                } else {
                    for (const item of this.selectedTiereGewichte) {
                        const gtId = Number(item?.tier?.gt_id);
                        const w = weightByGtId.get(gtId);
                        if (w !== undefined) {
                            item.gewichtCtrl.setValue(w);
                            gesetzt++;
                        }
                    }
                }
                if (gesetzt > 0) {
                    this.messageService.infoMessage(`Letzte Gewichte fuer ${gesetzt} Tier(e) geladen.`);
                } else {
                    this.messageService.infoMessage('Keine letzten Gewichte gefunden.');
                }
            },
            error: err => {
                this.messageService.errorMessage('Letzte Gewichte konnten nicht geladen werden!', err);
            },
        });
    }

    private toValidGewicht(value: any): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        const parsed = Number(String(value).replace(',', '.'));
        if (isNaN(parsed) || parsed <= 0) {
            return null;
        }
        return Math.round(parsed * 100) / 100;
    }

    get tierCountForWeightInfo(): number {
        return this.selectedTiereGewichte?.length || 0;
    }

    toggleWeightEntryMode(mode: Exclude<WeightEntryMode, null>): void {
        this.weightEntryMode = this.weightEntryMode === mode ? null : mode;
    }

    private getTierAbgangGewicht(tier: GruppeTier | null | undefined): number | null {
        if (!tier) {
            return null;
        }
        const raw = tier as GruppeTier & { abgangsgewicht?: number | string | null; ausstallgewicht?: number | string | null };
        return this.toValidGewicht(raw.ausstallgewicht ?? raw.abgangsgewicht);
    }

    private sumTierAbgangGewichte(): number | null {
        if (!this.selectedTiereGewichte?.length) {
            return null;
        }
        let sum = 0;
        let count = 0;
        for (const item of this.selectedTiereGewichte) {
            const gewicht = this.toValidGewicht(item.gewichtCtrl.value);
            if (gewicht !== null) {
                sum += gewicht;
                count++;
            }
        }
        return count > 0 ? Math.round(sum * 100) / 100 : null;
    }

    private applyLoadedAbgangGesamtgewicht(): void {
        const fromTierliste = this.toValidGewicht(this.tierliste?.gewicht_gesamt_abgang);
        const hasTierWeights = this.selectedTiereGewichte.some(
            item => this.toValidGewicht(item.gewichtCtrl.value) !== null,
        );
        if (hasTierWeights) {
            const fromTiere = this.sumTierAbgangGewichte();
            this.totalWeightCtrl.setValue(fromTiere ?? fromTierliste, { emitEvent: false });
            this.weightEntryMode = 'perAnimal';
            return;
        }

        if (fromTierliste !== null) {
            this.totalWeightCtrl.setValue(fromTierliste, { emitEvent: false });
            this.weightEntryMode = 'totalOnly';
            return;
        }

        this.totalWeightCtrl.setValue(null, { emitEvent: false });
        this.initializeWeightEntryMode();
    }

    private initializeWeightEntryMode(): void {
        if (this.toValidGewicht(this.totalWeightCtrl.value) !== null) {
            this.weightEntryMode = 'totalOnly';
            return;
        }

        const hasTierWeights = this.selectedTiereGewichte.some(item => this.toValidGewicht(item.gewichtCtrl.value) !== null);
        this.weightEntryMode = hasTierWeights ? 'perAnimal' : 'totalOnly';
    }

    private usesIndividualWeightMode(): boolean {
        return this.weightEntryMode === 'perAnimal' || this.weightEntryMode === 'samePerAnimal';
    }

    /** Apply form weights onto the tierliste. Returns Gesamt-Abgangsgewicht. */
    private applyAbgangGewichteToTierliste(): number | null {
        const gesamtAbgang = this.resolveAbgangGesamtgewichtForSave();
        this.tierliste.gewicht_gesamt_abgang = gesamtAbgang;
        return gesamtAbgang;
    }

    /**
     * Same rule as the dashboard weight dialog: ask only when a list total or a
     * shared per-animal weight would touch animals that already have a weight.
     * Per-animal entry keeps the typed values and does not open the dialog.
     */
    private async resolveAbgangGewichtWriteMode(): Promise<'keep' | BulkOverwriteMode | 'cancel'> {
        const field = this.buildAbgangBulkField();
        if (!field) {
            return 'keep';
        }
        const animals = this.bulkAnimalsFromSelection();
        if (!needsBulkOverwriteConfirm(animals, [field])) {
            return 'empty_only';
        }
        const result = await firstValueFrom(
            this.dialog
                .open(GruppeTierBulkOverwriteDialogComponent, {
                    width: '640px',
                    maxWidth: '95vw',
                    disableClose: true,
                    data: { fields: [field], animals },
                })
                .afterClosed(),
        );
        if (!result?.mode) {
            return 'cancel';
        }
        return result.mode;
    }

    private applyAbgangGewichtWriteMode(mode: 'keep' | BulkOverwriteMode): void {
        if (mode === 'keep') {
            return;
        }
        const field = this.buildAbgangBulkField();
        if (!field) {
            return;
        }
        const animals = this.bulkAnimalsFromSelection();
        const value = resolveBulkPerTierValue(mode, animals, field);
        this.selectedTiereGewichte.forEach((item, index) => {
            const animal = animals[index];
            const has = animal ? animalHasFieldValue(animal, 'abgangsgewicht') : false;
            if (mode === 'all') {
                if (value != null) {
                    item.gewichtCtrl.setValue(value, { emitEvent: false });
                }
                return;
            }
            if (!has && value != null) {
                item.gewichtCtrl.setValue(value, { emitEvent: false });
                return;
            }
            if (has && animal) {
                item.gewichtCtrl.setValue(getAnimalFieldValue(animal, 'abgangsgewicht') ?? null, { emitEvent: false });
            }
        });
    }

    private buildAbgangBulkField(): BulkOverwriteFieldSpec | null {
        if (this.weightEntryMode === 'totalOnly') {
            const gesamt = this.toValidGewicht(this.totalWeightCtrl.value);
            if (gesamt == null) {
                return null;
            }
            return {
                key: 'abgangsgewicht',
                label: 'Abgangsgewicht',
                gesamtValue: gesamt,
                preferProTier: false,
                unit: 'kg',
            };
        }
        if (this.weightEntryMode === 'samePerAnimal') {
            const pro = this.toValidGewicht(this.sameWeightCtrl.value);
            if (pro == null) {
                return null;
            }
            const count = this.selectedTiereGewichte?.length ?? 0;
            return {
                key: 'abgangsgewicht',
                label: 'Abgangsgewicht',
                gesamtValue: count > 0 ? Math.round(pro * count * 100) / 100 : null,
                proTierValue: pro,
                preferProTier: true,
                unit: 'kg',
            };
        }
        return null;
    }

    private bulkAnimalsFromSelection(): BulkOverwriteAnimalRow[] {
        return (this.selectedTiereGewichte || []).map(item =>
            mapGruppeTierRecordToBulkRow((item?.tier ?? {}) as unknown as Record<string, unknown>),
        );
    }

    private getAbgangGewichtByGtId(): Map<number, number> {
        const gewichtByGtId = new Map<number, number>();
        for (const item of this.selectedTiereGewichte || []) {
            const gtId = Number(item?.tier?.gt_id);
            const g = this.toValidGewicht(item.gewichtCtrl.value);
            if (gtId > 0 && g !== null) {
                gewichtByGtId.set(gtId, g);
            }
        }
        return gewichtByGtId;
    }

    private resolveAbgangGesamtgewichtForSave(): number | null {
        if (this.weightEntryMode === 'totalOnly') {
            return this.toValidGewicht(this.totalWeightCtrl.value);
        }
        if (!this.selectedTiereGewichte?.length) {
            return null;
        }
        const werte = this.selectedTiereGewichte
            .map(item => this.toValidGewicht(item.gewichtCtrl.value))
            .filter((v): v is number => v !== null);
        if (!werte.length) {
            return null;
        }
        return Math.round(werte.reduce((a, b) => a + b, 0) * 100) / 100;
    }

    private ladeTiereAusAktuellerTierliste(): void {
        const tierlisteId = Number(this.tierliste?.tierliste_id) || 0;
        if (tierlisteId <= 0) {
            return;
        }
        const tiereParams = new HttpParams().set('tierliste_id', String(tierlisteId));
        const listeParams = new HttpParams().set('tierliste_id', String(tierlisteId));
        forkJoin({
            tiere: this.api.getAll(this.apiBaseUrl + 'gruppe_tier/read_kundenportal.php', tiereParams),
            tierliste: this.api.get(this.apiBaseUrl + 'tierliste/read_one_kundenportal.php', listeParams),
        }).subscribe({
            next: ({ tiere, tierliste }) => {
                if (tierliste?.tierliste_id) {
                    this.tierliste = { ...this.tierliste, ...tierliste };
                }
                this.selectedTiereGewichte = (tiere?.records || [])
                    .filter(t => Number(t?.gt_id) > 0)
                    .map(t => ({
                        tier: t,
                        gewichtCtrl: new FormControl(this.getTierAbgangGewicht(t)),
                    }));
                this.applyLoadedAbgangGesamtgewicht();
            },
            error: err => {
                this.messageService.errorMessage('Tiere der Ausstallliste konnten nicht geladen werden!', err);
            },
        });
    }

    private async speichereAbgangsgewichteFuerTiere(): Promise<void> {
        if (!this.selectedTiereGewichte?.length) {
            return;
        }
        const gewichtByGtId = new Map<number, number>();
        for (const item of this.selectedTiereGewichte) {
            const gtId = Number(item?.tier?.gt_id);
            const g = this.toValidGewicht(item.gewichtCtrl.value);
            if (gtId > 0 && g !== null) {
                gewichtByGtId.set(gtId, g);
            }
        }
        if (gewichtByGtId.size === 0) {
            return;
        }

        let params = new HttpParams();
        params = params.set('gt_ids', Array.from(gewichtByGtId.keys()).join(','));
        const standorteRes: any = await this.api
            .getAll(this.apiBaseUrl + 'gruppe/read_gt_letzter_standort.php', params)
            .toPromise();
        const gsByGtId = new Map<number, number>();
        for (const rec of standorteRes?.records || []) {
            const gtId = Number(rec?.gt_id);
            const gsId = Number(rec?.gs_id);
            if (gtId > 0 && gsId > 0) {
                gsByGtId.set(gtId, gsId);
            }
        }

        for (const [gtId, gewicht] of gewichtByGtId.entries()) {
            const gsId = gsByGtId.get(gtId);
            if (gsId && gsId > 0) {
                await this.api
                    .post(this.apiBaseUrl + 'gruppe_standort_tier/update.php', {
                        gs_id: gsId,
                        gt_id: gtId,
                        abgangsgewicht: gewicht,
                    })
                    .toPromise();
            }
        }
    }

    private async loadGewichtErlaubteBetriebe(): Promise<void> {
        try {
            const { meine, freigaben } = await firstValueFrom(
                forkJoin({
                    meine: this.api.getAll(this.apiBaseUrl + 'kunde/read_kundenportal_meine.php'),
                    freigaben: this.api.getAll(this.apiBaseUrl + 'kunde/read_kundenportal_freigaben.php'),
                }),
            );
            const ids = [...(meine?.records || []), ...(freigaben?.records || [])]
                .map(v => Number(v?.kunde_id))
                .filter(v => v > 0);
            this.gewichtErlaubteKundeIds = new Set(ids);
        } catch {
            this.gewichtErlaubteKundeIds = new Set<number>();
        }
    }

    private isGewichtTransferErlaubt(): boolean {
        if (!this.data?.intern) {
            return false;
        }
        const zielKundeId = Number(this.zielbetrieb?.kunde_id);
        if (zielKundeId <= 0) {
            return false;
        }
        return this.gewichtErlaubteKundeIds.has(zielKundeId);
    }

    private hasRequiredAusstalldatum(): boolean {
        if (!this.embeddedInStepper) {
            return true;
        }
        const value = this.datumCtrl.value;
        return value != null && value !== '';
    }

    private validateRequiredAusstalldatum(): boolean {
        if (this.hasRequiredAusstalldatum()) {
            return true;
        }
        this.datumCtrl.markAsTouched();
        const message = this.getRequiredAusstalldatumMessage();
        if (message) {
            this.messageService.alertMessage(message);
        }
        return false;
    }

    private getRequiredAusstalldatumMessage(): string {
        return 'Bitte ein Ausstalldatum angeben.';
    }

    private hasRequiredEmpfaengerSelection(): boolean {
        if (this.mobileFinishMode) {
            return true;
        }
        if (this.data?.intern) {
            if (!this.hasTiereOnTierliste) {
                return true;
            }
            return this.getZielKundeIdForSave() !== null;
        }
        return true;
    }

    private validateBeforeSave(): boolean {
        if (!this.validateRequiredAusstalldatum()) {
            return false;
        }
        const tiereMessage = this.getRequiredTiereMessage();
        if (tiereMessage) {
            this.messageService.alertMessage(tiereMessage);
            return false;
        }
        return this.validateRequiredEmpfaengerSelection();
    }

    private getTiereCountOnTierliste(): number {
        const fromListe = Number(this.tierliste?.anzahl_tiere ?? 0);
        if (fromListe > 0) {
            return fromListe;
        }
        const fromData = (this.data?.tiere || []).filter(t => Number(t?.gt_id) > 0).length;
        if (fromData > 0) {
            return fromData;
        }
        return this.selectedTiereGewichte?.length ?? 0;
    }

    private getRequiredTiereMessage(): string | null {
        if (!this.data?.intern) {
            return null;
        }
        if (this.hasTiereOnTierliste) {
            return null;
        }
        return 'Bitte zuerst Tiere zur Ausstallliste hinzufügen. Danach können Sie Zielbetrieb und Abgangsgewicht festlegen.';
    }

    private validateRequiredEmpfaengerSelection(): boolean {
        if (this.hasRequiredEmpfaengerSelection()) {
            return true;
        }

        const message = this.getRequiredEmpfaengerSelectionMessage();
        if (message) {
            this.messageService.alertMessage(message);
        }

        return false;
    }

    private getRequiredEmpfaengerSelectionMessage(): string {
        if (this.data?.intern) {
            return 'Bitte einen Zielbetrieb auswählen.';
        }

        return 'Bitte mindestens einen Kontakt oder einen Schlachthof auswählen.';
    }

    private getZielKundeIdForSave(): number | null {
        if (this.zielbetriebCleared) {
            return null;
        }
        const zielbetriebKundeId = Number(this.zielbetrieb?.kunde_id);
        if (zielbetriebKundeId > 0) {
            return zielbetriebKundeId;
        }

        const kontaktKundeId = Number(this.selectedKontakte?.[0]?.kunde_id);
        if (kontaktKundeId > 0) {
            return kontaktKundeId;
        }

        const existingKundeId = Number(this.tierliste?.kunde_id_einstallen);
        if (existingKundeId > 0) {
            return existingKundeId;
        }

        return null;
    }

    /**
     * Einfache Überprüfung: Sind Daten in der benutzer_betriebskontakte_tierliste Tabelle?
     */
    private ueberpruefeTabelle(tierlisteId: number): void {
        // Sicherheitscheck
        if (!tierlisteId || tierlisteId <= 0) {
            return;
        }

        // Einfacher API-Aufruf nur zum Testen
        const params = new HttpParams().set('tierliste_id', tierlisteId.toString());
        this.api
            .getAll(this.apiBaseUrl + 'benutzer_betriebskontakte_tierliste/read.php', params)
            .subscribe({ next: result => {}, error: err => {} });
    }

    // Alte Delete-Methoden für Händler und Zielbetrieb entfernt

    deleteSchlachthof() {
        this.schlachthof = null;
    }

    clearZielbetrieb(): void {
        this.zielbetrieb = null;
        this.zielbetriebCleared = true;
        this.clearInternZielbetriebDisplayFields();
        this.rebuildEmpfaengerDisplayEntries();
    }

    zielbetriebAendern() {
        const inhaberKundeId =
            Number(this.ausstallenderBetrieb?.kunde_id ?? this.tierliste?.kunde_id_abgang) || null;
        this.dialog
            .open(AusstallenEmpfaengerDialogComponent, {
                width: '760px',
                maxWidth: 'calc(100vw - 24px)',
                maxHeight: '90vh',
                panelClass: ['ausstallen-empfaenger-dialog-panel', 'letzte-aktionen-empfaenger-dialog-panel'],
                data: {
                    tierliste: this.tierliste,
                    gruppeKundeId: this.data?.gruppe_abgang?.gruppe_id,
                    flowType: 'umstallen_intern',
                    inhaberKundeId,
                },
                disableClose: false,
            })
            .afterClosed()
            .subscribe({
                next: (result: AusstallenEmpfaengerDialogResult | undefined) => {
                    if (!result) {
                        return;
                    }
                    if (result.zielbetrieb) {
                        this.zielbetrieb = result.zielbetrieb;
                        this.zielbetriebCleared = false;
                        this.tierliste = result.tierliste;
                        if (this.showHitAbgangMelden) {
                            this.loadHitMeldenPreference();
                        }
                        return;
                    }
                    this.clearZielbetrieb();
                },
            });
    }

    private setAusstallenderBetrieb(betrieb: Client): void {
        if (!betrieb) {
            return;
        }
        this.ausstallenderBetrieb = betrieb;
        this.tierliste.kunde_id_abgang = betrieb.kunde_id;
        const bezeichnung = betrieb.bezeichnung_final ?? betrieb.bezeichnung ?? '';
        const zusatz = betrieb.zusatz_final ?? betrieb.zusatz ?? '';
        this.tierliste.kunde_abgang = bezeichnung;
        this.tierliste.kunde_zusatz_abgang = zusatz;
        this.tierliste.kunden_abgang = zusatz ? `${bezeichnung} ${zusatz}`.trim() : bezeichnung;
        if (!this.data?.gruppe_abgang) {
            this.tierliste.mehrere_gruppen = 1;
        }
    }

    openContactDialog(contactSelector: ContactSelector) {
        let input;
        if (contactSelector === 'Schlachthof') {
            input = new MeineKontakteDialogInput('Schlachthof', false, false, true, this.data?.gruppe_abgang?.kunde_id);
        }

        this.dialog
            .open<MeineKontakteDialogComponent, MeineKontakteDialogInput, MeineKontakteDialogResult>(
                MeineKontakteDialogComponent,
                { maxWidth: '100vh', maxHeight: '90vh', width: '100%', data: input, disableClose: false },
            )
            .afterClosed()
            .subscribe(result => {
                if (result?.kunde) {
                    if (contactSelector == 'Schlachthof') {
                        this.schlachthof = result.kunde;
                    }
                }
            });
    }

    // Neue Methoden für Multi-Empfänger
    onKontakteAusgewaehlt(kontakte: Betriebskontakt[]): void {
        this.selectedKontakte = [...kontakte];
        this.tempSelectedKontakte = [...kontakte];
        this.showKontakteAuswahl = false;
        this.showSelectedKontakte = true;
        this.rebuildEmpfaengerDisplayEntries();
        this.persistAusstallenOnChange('empfaenger');
    }

    onKontakteSelectionChanged(kontakte: Betriebskontakt[]): void {
        this.tempSelectedKontakte = [...kontakte];
    }

    removeKontakt(kontakt: Betriebskontakt): void {
        // Entferne aus selectedKontakte (aktuelle Anzeige)
        const index = this.selectedKontakte.findIndex(k => KieneUtils.sameId(k.kontakt_id, kontakt.kontakt_id));
        if (index >= 0) {
            this.selectedKontakte.splice(index, 1);
        }

        // Entferne auch aus tempSelectedKontakte (für Bearbeitungsmodus)
        const tempIndex = this.tempSelectedKontakte.findIndex(k => KieneUtils.sameId(k.kontakt_id, kontakt.kontakt_id));
        if (tempIndex >= 0) {
            this.tempSelectedKontakte.splice(tempIndex, 1);
        }

        // Synchronisiere tempSelectedKontakte mit selectedKontakte
        this.tempSelectedKontakte = [...this.selectedKontakte];
        this.rebuildEmpfaengerDisplayEntries();

        // Wenn alle Kontakte entfernt wurden, zeige trotzdem die Kontakte-Ansicht
        // damit der Benutzer speichern kann (leeres Array wird dann gespeichert)
        if (this.selectedKontakte.length === 0) {
            this.showKontakteAuswahl = false;
            this.showSelectedKontakte = true; // Zeige leere Kontakte-Ansicht
        }

        // Debug-Log für die Entfernung
        console.log('Kontakt entfernt:', kontakt.bezeichnung, 'Verbleibende Kontakte:', this.selectedKontakte.length);
        this.persistAusstallenOnChange('empfaenger');
    }

    // Kontakt zu den eigenen Kontakten hinzufügen
    async kontaktHinzufuegen(kontakt: Betriebskontakt): Promise<void> {
        try {
            // Verwende den BetriebskontakteService um den Kontakt hinzuzufügen
            const result = await this.betriebskontakteService.fuegeKontaktHinzu(kontakt.kunde_id, false).toPromise();

            if (result) {
                this.messageService.infoMessage('Kontakt erfolgreich zu Ihren Kontakten hinzugefügt!');

                // Nach dem Hinzufügen den Kontakt als "bereits vorhanden" markieren
                this.markiereKontaktAlsBereitsVorhanden(kontakt);
            } else {
            }
        } catch (error) {
            this.messageService.errorMessage('Fehler beim Hinzufügen des Kontakts', error);
        }
    }

    // Prüft, welche Kontakte bereits in den eigenen Kontakten vorhanden sind
    private async pruefeKontakteBereitsVorhanden(kontakte: Betriebskontakt[]): Promise<void> {
        try {
            // Lade alle eigenen Kontakte
            const eigeneKontakte = await this.betriebskontakteService.getBetriebskontakte().toPromise();
            const eigeneKundeIds = eigeneKontakte.records.map(k => k.kunde_id);

            // Speichere die eigenen Kunde-IDs für die Template-Logik
            this.eigeneKontaktIds = eigeneKundeIds;
        } catch (error) {
            this.eigeneKontaktIds = [];
        }
    }

    // Markiert einen Kontakt als "bereits vorhanden" nach dem Hinzufügen
    private markiereKontaktAlsBereitsVorhanden(kontakt: Betriebskontakt): void {
        // Aktualisiere die eigenen Kunde-IDs
        if (kontakt.kunde_id && !KieneUtils.includesId(this.eigeneKontaktIds, kontakt.kunde_id)) {
            this.eigeneKontaktIds.push(kontakt.kunde_id);
        }
    }

    private async speichereKontakteInTabelle(
        tierlisteId: number,
        kontakte: Betriebskontakt[] = this.selectedKontakte,
        options?: { allowClearAll?: boolean },
    ): Promise<void> {
        try {
            const kontaktIds = kontakte?.filter(k => k.kontakt_id).map(k => k.kontakt_id!) || [];
            if (kontaktIds.length === 0 && !options?.allowClearAll) {
                return;
            }
            const kontaktKundeIds: Record<number, number> = {};
            for (const kontakt of kontakte || []) {
                const kontaktId = Number(kontakt?.kontakt_id);
                const kundeId = Number(kontakt?.kunde_id);
                if (kontaktId > 0 && kundeId > 0) {
                    kontaktKundeIds[kontaktId] = kundeId;
                }
            }

            await this.bbtService
                .updateKontakteFuerTierliste(kontaktIds, tierlisteId, {
                    allowClearAll: options?.allowClearAll ?? kontaktIds.length === 0,
                    kontaktKundeIds,
                })
                .toPromise();
        } catch (error) {
            throw error;
        }
    }

    kontakteBearbeiten(): void {
        if (this.useModalEmpfaengerPicker) {
            this.openEmpfaengerModal();
            return;
        }
        this.tempSelectedKontakte = [...(this.selectedKontakte || [])];
        this.showKontakteAuswahl = true;
        this.showSelectedKontakte = false;
    }

    openEmpfaengerModal(event?: Event): void {
        if (event instanceof KeyboardEvent && event.key === ' ') {
            event.preventDefault();
        }
        if (this.hasActiveTextSelection()) {
            return;
        }
        if (this.isSaving) {
            return;
        }
        this.dialog
            .open(AusstallenEmpfaengerDialogComponent, {
                width: '760px',
                maxWidth: 'calc(100vw - 24px)',
                maxHeight: '90vh',
                panelClass: ['ausstallen-empfaenger-dialog-panel', 'letzte-aktionen-empfaenger-dialog-panel'],
                data: {
                    tierliste: this.tierliste,
                    gruppeKundeId: this.data?.gruppe_abgang?.gruppe_id,
                    flowType: 'ausstallen_extern',
                },
            })
            .afterClosed()
            .subscribe({
                next: (result: AusstallenEmpfaengerDialogResult) => {
                    if (!result) {
                        return;
                    }
                    this.applyEmpfaengerModalResult(result);
                },
            });
    }

    /** Do not open picker when the user is selecting/copying Empfänger text on the card. */
    private hasActiveTextSelection(): boolean {
        const selection = window.getSelection();
        return !!selection && selection.toString().trim().length > 0;
    }

    private applyEmpfaengerModalResult(result: AusstallenEmpfaengerDialogResult): void {
        this.schlachthof = (result.schlachthof ?? null) as Client;
        this.selectedKontakte = result.kontakte ?? [];
        this.tempSelectedKontakte = [...this.selectedKontakte];
        this.tierliste = patchTierlisteFromExternEmpfaenger(
            result.tierliste,
            this.selectedKontakte,
            this.schlachthof ?? null,
        );
        if (!this.schlachthof && this.selectedKontakte.length === 0) {
            this.tierliste.kunde_zielbetrieb = null;
            this.tierliste.kunde_zusatz_zielbetrieb = null;
            this.tierliste.kunde_vvvo_zielbetrieb = null;
            this.tierliste.kunde_strasse_zielbetrieb = null;
            this.tierliste.kunde_plz_zielbetrieb = null;
            this.tierliste.kunde_ort_zielbetrieb = null;
        }
        this.rebuildEmpfaengerDisplayEntries();
    }

    // Unique by kontakt_id — multiple Empfänger may share the same kunde_id.
    private entferneDuplikate(kontakte: Betriebskontakt[]): Betriebskontakt[] {
        const seen = new Set<number>();
        const result: Betriebskontakt[] = [];
        for (const kontakt of kontakte) {
            const kontaktId = kontakt.kontakt_id || 0;
            if (kontaktId > 0 && !seen.has(kontaktId)) {
                seen.add(kontaktId);
                result.push(kontakt);
            }
        }
        return result;
    }

    private async ladeGespeicherteKontakte(tierlisteId: number): Promise<void> {
        if (!tierlisteId) {
            this.gespeicherteKontakteLoaded = true;
            return;
        }

        try {
            const result = await this.bbtService.getVerbindungenFuerTierliste(tierlisteId).toPromise();
            const aktiveVerbindungen = filterAktiveBetriebskontakteTierlisteVerbindungen(
                unwrapBetriebskontakteTierlisteReadResponse(result),
            );

            if (aktiveVerbindungen && aktiveVerbindungen.length > 0) {
                const kontakte = await this.ladeKontakteAusVerbindungen(aktiveVerbindungen);

                if (kontakte.length > 0) {
                    const eindeutigeKontakte = this.entferneDuplikate(kontakte);

                    this.selectedKontakte = eindeutigeKontakte;
                    this.tempSelectedKontakte = [...eindeutigeKontakte];
                    this.kontakteConfig.zusaetzlicheKontakte = eindeutigeKontakte;

                    await this.pruefeKontakteBereitsVorhanden(eindeutigeKontakte);

                    this.applyKontakteLoadedUiState();
                } else {
                    this.applyKontakteLoadedUiState();
                }
            } else {
                this.applyKontakteLoadedUiState();
            }
        } catch (error) {
            this.applyKontakteLoadedUiState();
        } finally {
            this.gespeicherteKontakteLoaded = true;
        }
    }

    private applyKontakteLoadedUiState(): void {
        if (this.useModalEmpfaengerPicker) {
            this.showKontakteAuswahl = false;
            this.showSelectedKontakte = false;
            this.rebuildEmpfaengerDisplayEntries();
            return;
        }
        this.showKontakteAuswahl = false;
        this.showSelectedKontakte = true;
        this.rebuildEmpfaengerDisplayEntries();
    }

    // Hilfsmethode: Vollständige Kontaktdaten aus den Verbindungen laden
    private async ladeKontakteAusVerbindungen(verbindungen: any[]): Promise<Betriebskontakt[]> {
        return mapBetriebskontakteTierlisteVerbindungenToKontakte(verbindungen);
    }
}
